package com.havn.app.core.billing

import com.havn.app.core.supabase.havnRevenueCatWebProMonthlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebProYearlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebPurchaseLink
import com.havn.app.core.supabase.havnRevenueCatWebSoloMonthlyPackageId
import com.havn.app.core.supabase.havnRevenueCatWebSoloYearlyPackageId
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import kotlinx.browser.window

actual fun createHavnBillingService(): HavnBillingService = object : HavnBillingService {
    override suspend fun loadPlans(appUserId: String): Result<HavnStorePlans> =
        Result.success(
            HavnStorePlans(
                options = listOf(
                    HavnStorePlanOption(
                        id = "web_solo_monthly",
                        tier = HavnSubscriptionTier.SoloAgent,
                        period = HavnBillingPeriod.Monthly,
                        price = "\$9.99",
                        productId = havnRevenueCatWebSoloMonthlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_solo_yearly",
                        tier = HavnSubscriptionTier.SoloAgent,
                        period = HavnBillingPeriod.Yearly,
                        price = "\$95.90",
                        monthlyEquivalentPrice = "\$7.99",
                        productId = havnRevenueCatWebSoloYearlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_pro_monthly",
                        tier = HavnSubscriptionTier.ProAgent,
                        period = HavnBillingPeriod.Monthly,
                        price = "\$19.99",
                        productId = havnRevenueCatWebProMonthlyPackageId
                    ),
                    HavnStorePlanOption(
                        id = "web_pro_yearly",
                        tier = HavnSubscriptionTier.ProAgent,
                        period = HavnBillingPeriod.Yearly,
                        price = "\$191.90",
                        monthlyEquivalentPrice = "\$15.99",
                        productId = havnRevenueCatWebProYearlyPackageId
                    )
                )
            )
        )

    override suspend fun purchase(option: HavnStorePlanOption, appUserId: String): Result<HavnBillingCustomerState> =
        runCatching {
            val baseLink = havnRevenueCatWebPurchaseLink.trim()
            require(baseLink.isNotBlank()) {
                "Missing web purchase link. Add HAVN_REVENUECAT_WEB_PURCHASE_LINK after creating a RevenueCat Web Purchase Link."
            }
            val checkoutUrl = buildRevenueCatWebPurchaseUrl(
                baseLink = baseLink,
                appUserId = appUserId,
                packageId = option.productId
            )
            check(window.open(checkoutUrl, "_blank") != null) {
                "Unable to open checkout. Allow popups for HAVN and try again."
            }
            HavnBillingCustomerState()
        }

    override suspend fun restorePurchases(appUserId: String): Result<HavnBillingCustomerState> =
        Result.failure(IllegalStateException("Web purchases sync after checkout. Sign in with the same HAVN account, or use iOS Restore Purchases to check App Store purchases."))
}

private fun buildRevenueCatWebPurchaseUrl(
    baseLink: String,
    appUserId: String,
    packageId: String
): String {
    val baseWithoutSlash = baseLink.trimEnd('/')
    val linkWithUser = "$baseWithoutSlash/${encode(appUserId)}"
    val query = listOfNotNull(
        packageId.takeIf { it.isNotBlank() }?.let { "package_id=${encode(it)}" }
    ).joinToString("&")
    return if (query.isBlank()) linkWithUser else "$linkWithUser?$query"
}

@Suppress("UnsafeCastFromDynamic")
private fun encode(value: String): String = js("encodeURIComponent(value)") as String
